import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import * as jsonManager from '../../abis/Manager.json';
import { environment } from '../../../environments/environment';
import { ConnectionsService } from '../../services/connectionsWeb3/connections.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UserTransferDto } from '../../interfaces/user-transfer.dto';
import { DigitalWalletService } from '../../services/digital-wallet.service';
import { AuthService } from '../../services/auth.service';
import { TwoFactorAuthService } from '../../services/two-factor/two-factor-auth.service';

@Component({
  selector: 'app-transfer-batch',
  templateUrl: './transfer-batch.component.html',
  styleUrls: ['./transfer-batch.component.css'],
})
export class TransferBatchComponent implements OnInit {
  formTransferBatch: FormGroup = this.createFormTransferBatch();

  ourAddress: string | undefined;
  web3: any;
  abiManager: any = jsonManager;
  contractManager: any;
  tokenToSellerMap: { [tokenAddress: string]: string } = {};
  mappingId = new Map();

  availableWallets: {
    id: string;
    name: string;
  }[] = [];
  transfers: UserTransferDto[] = [];
  selectedWalletId: string | undefined;
  verified: boolean = false;
  transferred: boolean = false;
  blockVerify: boolean = true;
  blockTransfer: boolean = true;

  constructor(
    private readonly connSrv: ConnectionsService,
    private readonly walletService: DigitalWalletService,
    private readonly authService: AuthService,
    private readonly twoFactorService: TwoFactorAuthService,
    private readonly router: Router,
    private readonly afs: AngularFirestore
  ) {
    this.web3 = this.connSrv.web3Instance;

    this.connSrv.addressUser.subscribe((res: string) => {
      this.ourAddress = res;
    });

    this.contractManager = new this.web3.eth.Contract(
      this.abiManager.abi,
      environment.config.ADDRESS_MANAGER
    );
  }

  ngOnInit() {
    this.loadWallets();

    this.wallet?.valueChanges.subscribe((selectedWallet) => {
      const wallet = this.availableWallets.find(
        (wallet) => wallet.id === selectedWallet
      );
      this.selectedWalletId = wallet?.id;
      this.verifyStatus(true);
    });
  }

  verifyStatus(forceChange: boolean = false) {
    if (forceChange) {
      this.verified = false;
      this.transferred = false;
    }
    this.blockVerify =
      !this.selectedWalletId || this.transfers.length == 0 || this.verified;
    this.blockTransfer =
      !this.selectedWalletId ||
      this.transfers.length == 0 ||
      !this.verified ||
      this.transferred;
  }

  createFormTransferBatch() {
    return new FormGroup({
      wallet: new FormControl('', [Validators.required]),
    });
  }

  get wallet() {
    return this.formTransferBatch?.get('wallet');
  }

  onListTransfersChange(transfers: UserTransferDto[]) {
    this.transfers = transfers;
    this.verifyStatus(true);
  }

  private loadWallets() {
    this.availableWallets = [
      {
        id: '1',
        name: 'Dividendos',
      },
      {
        id: '3',
        name: 'Referidos',
      },
    ];
  }

  formatToWei(num: number) {
    return this.web3.utils.toWei(num.toString(), 'ether');
  }

  getCheckText(error: string = ''): string {
    if (error === 'no configurado') {
      return 'Billetera del sistema no configurada';
    } else if (error === 'no existe') {
      return 'Billetera del sistema no existe en Mangopay';
    } else if (error === 'no fondos') {
      return 'No hay saldo suficiente para honrar las transferencias';
    } else if (error === 'user error') {
      return 'Errores en la lista de solicitudes';
    } else return '';
  }

  private async checkMfaEnabled(): Promise<boolean> {
    const currentUser = this.authService.userAuth.getValue();
    if (!currentUser?.uid) return false;
    const enabled = await this.twoFactorService.isEnabled(currentUser.uid);
    if (!enabled) {
      const result = await Swal.fire({
        title: 'Autenticación en dos pasos requerida',
        html: 'Esta operación requiere que tengas el <b>doble factor de autenticación (2FA)</b> activado.<br><br>Ve a tu perfil y actívalo antes de continuar.',
        icon: 'warning',
        confirmButtonText: 'Ir a configuración',
        confirmButtonColor: '#0093A2',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
      });
      if (result.isConfirmed) {
        this.router.navigate(['/perfil/configuracion/autenticacion-dos-factores']);
      }
      return false;
    }
    return true;
  }

  // Función para verificar si un usuario existe en Firestore
  async verifyUserExists(email: string): Promise<boolean> {
    try {
      const userQuery = await this.afs.collection('users', ref =>
        ref.where('email', '==', email).limit(1)
      ).get().toPromise();

      return !!(userQuery && !userQuery.empty);
    } catch (error) {
      console.error('Error verificando usuario en Firestore:', error);
      return false;
    }
  }

  async checkTransfers() {
    if (!await this.checkMfaEnabled()) return;

    Swal.fire({
      title: 'Verificando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Verificar que el usuario esté autenticado
      const currentUser = this.authService.userAuth.getValue();
      console.log('[checkTransfers] Usuario autenticado:', currentUser ? 'Sí' : 'No');
      
      if (!currentUser) {
        Swal.close();
        Swal.fire({
          title: 'Error de autenticación',
          text: 'Debes estar autenticado para realizar esta operación.',
          icon: 'error',
        });
        return;
      }

      console.log('[checkTransfers] Verificando transferencias:', this.transfers);
      console.log('[checkTransfers] Wallet seleccionada:', this.selectedWalletId);

      // Verificar usuarios en Firestore antes de enviar al backend
      for (let transfer of this.transfers) {
        const userExists = await this.verifyUserExists(transfer.email);
        console.log(`[checkTransfers] Usuario ${transfer.email} existe en Firestore:`, userExists);

        if (!userExists) {
          transfer.error = 'unknown';
        }
      }

      console.log('[checkTransfers] Enviando petición al backend...');
      const result = await this.walletService.checkBatchTransfers(
        this.transfers,
        this.selectedWalletId!
      );

      console.log('[checkTransfers] Resultado de verificación:', result);

      Swal.close();

      this.transfers = result.transfers;

      if (result.error === '') {
        this.verified = true;
        Swal.fire({
          title: 'Verificación exitosa',
          text: 'Las inversiones han sido verificadas correctamente.',
          icon: 'success',
        });
      } else {
        Swal.fire({
          title: 'Error de verificación',
          text: this.getCheckText(result.error),
          icon: 'error',
        });
      }
    } catch (error) {
      console.error('Error al verificar inversiones:', error);
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al verificar las inversiones.',
        icon: 'error',
      });
      return;
    }

    this.verifyStatus();
  }

  async processTransfers() {
    if (!await this.checkMfaEnabled()) return;
    await this.executeTransfers();
  }


  async executeTransfers() {
    const confirmResult = await Swal.fire({
      title: 'Procesar transferencias',
      html: `¿Estás seguro de que quieres procesar las transferencias? Este proceso no es reversible.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló la operación
    }
    console.log('[executeTransfers] Transfers:', this.transfers);
    console.log('[executeTransfers] Wallet ID:', this.selectedWalletId);

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const result = await this.walletService.processBatchTransfers(
      this.transfers,
      this.selectedWalletId!
    );

    Swal.close();

    console.log('Resultado de la verificación:', result);

    if (result.error === '') {
      this.transferred = true;
      this.verifyStatus();
      Swal.fire({
        title: 'Transferencias exitosas',
        text: 'Los fondos han sido transferidos correctamente.',
        icon: 'success',
      });
    } else {
      this.transfers = result.transfers;
      Swal.fire({
        title: 'Error en las transferencias',
        text: 'No se pudieron procesar las transferencias de todos los usuarios',
        icon: 'error',
      });
    }
    this.verifyStatus();
  }
}
